import { batch, createMemo, createEffect, onCleanup, onMount } from 'solid-js';
import { createStore } from 'solid-js/store';
import { createActor } from 'xstate';

function isWrappable(obj) {
  let proto;
  return obj != null && typeof obj === 'object' && (!(proto = Object.getPrototypeOf(obj)) || proto === Object.prototype || Array.isArray(obj));
}

/**
 * Accepts any value and creates a deep clone if it is an object
 * This function only deeply clones objects, any classes with be copied
 * @param value The variable to deeply clone
 * @param valueRefs A WeakMap that stores a reference from the original
 * object/array to the cloned object/array
 */
const clone = (value, valueRefs) => {
  if (!isWrappable(value)) {
    return value;
  }
  const isObject = !Array.isArray(value);

  // Get either a new object/array and a typed iterator
  const [clonedValue, keyedValues] = isObject ? [{}, Object.keys(value)] : [[], value];

  // Save a reference of the object/array
  valueRefs.set(value, clonedValue);

  // Loop over all object/array indexes and clone
  for (let i = 0; i < keyedValues.length; ++i) {
    const keyedIndex = isObject ? keyedValues[i] : i;
    const currentVal = value[keyedIndex];
    // Check if reference already exists, helps prevent max call stack
    if (valueRefs.has(currentVal)) {
      clonedValue[keyedIndex] = valueRefs.get(currentVal);
    } else {
      clonedValue[keyedIndex] = clone(currentVal, valueRefs);
    }
  }
  return clonedValue;
};
const deepClone = value => clone(value, new WeakMap());

const resolvePath = (path, obj = {}) => {
  let current = obj;
  // tslint:disable-next-line:prefer-for-of
  for (let i = 0; i < path.length; i++) {
    current = current?.[path[i]];
  }
  return current;
};
const updateStore = (nextStore, prevStore, set, store) => {
  const valueRefs = new WeakMap();
  const diff = (next, prev, path) => {
    if (prev === next) {
      return;
    }

    // Use reference if it has already been used circular reference loops
    if (valueRefs.has(next)) {
      set(...path, valueRefs.get(next));
      return;
    }
    if (!isWrappable(next) || !isWrappable(prev)) {
      // toString cannot be set in solid stores
      if (path[path.length - 1] !== 'toString') {
        set(...path, () => next);
      }
      return;
    }

    // next is either an object or array, save reference to prevent diffing
    // the same object twice
    valueRefs.set(next, resolvePath(path, store));

    // Diff and update array or object
    if (Array.isArray(next) && Array.isArray(prev)) {
      const newIndices = next.length - prev.length;
      const smallestSize = Math.min(prev.length, next.length);
      const largestSize = Math.max(next.length, prev.length);

      // Diff array
      for (let start = 0, end = largestSize - 1; start <= end; start++, end--) {
        diff(next[start], prev[start], [...path, start]);
        if (start === end) break;
        diff(next[end], prev[end], [...path, end]);
      }

      // Update new or now undefined indexes
      if (newIndices !== 0) {
        for (let newEnd = smallestSize; newEnd <= largestSize - 1; newEnd++) {
          set(...path, newEnd, next[newEnd]);
        }
        if (prev.length > next.length) {
          set(...path, 'length', next.length);
        }
      }
    } else {
      // Update new values
      const targetKeys = Object.keys(next);
      for (let i = 0, len = targetKeys.length; i < len; i++) {
        diff(next[targetKeys[i]], prev[targetKeys[i]], [...path, targetKeys[i]]);
      }

      // Remove previous keys that are now undefined
      const previousKeys = Object.keys(prev);
      for (let i = 0, len = previousKeys.length; i < len; i++) {
        if (next[previousKeys[i]] === undefined) {
          set(...path, previousKeys[i], undefined);
        }
      }
    }
  };
  diff(nextStore, prevStore, []);
};

/**
 * Based on Ryan Carniato's createImmutable prototype
 * Clones the initial value and diffs updates
 */
function createImmutable(init) {
  const [store, setStore] = createStore(deepClone(init));
  let ref = init;
  const setImmutable = next => {
    batch(() => {
      updateStore(next, ref, setStore, store);
    });
    ref = next;
  };
  return [store, setImmutable];
}

const noop = () => {};
function fromActorRef(actorRef) {
  const actorMemo = createMemo(() => typeof actorRef === 'function' ? actorRef() : actorRef);
  const [snapshot, setSnapshot] = createImmutable({
    v: actorMemo()?.getSnapshot()
  });
  createEffect(isInitialActor => {
    const currentActor = actorMemo();
    if (!isInitialActor) {
      setSnapshot({
        v: currentActor?.getSnapshot()
      });
    }
    if (currentActor) {
      const {
        unsubscribe
      } = currentActor.subscribe({
        next: nextSnapshot => setSnapshot({
          v: nextSnapshot
        }),
        error: noop,
        complete: noop
      });
      onCleanup(unsubscribe);
    }
    return false;
  }, true);
  return () => snapshot.v;
}

function useActorRef(logic, options) {
  const actorRef = createActor(logic, options);
  onMount(() => {
    actorRef.start();
    onCleanup(() => actorRef.stop());
  });
  return actorRef;
}

function useActor(logic, options) {
  const actorRef = useActorRef(logic, options);
  return [fromActorRef(actorRef)(), actorRef.send, actorRef];
}

/** @alias useActor */
function useMachine(machine, options) {
  return useActor(machine, options);
}

export { fromActorRef, useActor, useActorRef, useMachine };
